// Touch gesture recognizer: turns raw touch pointers into remote mouse/wheel/pinch
// actions. Pure state machine (time is injected) so it can be unit-tested.
import type { InputMsg } from './protocol';

export interface Pt {
  id: number;
  x: number; // remote CSS px
  y: number;
}

export interface GestureOptions {
  tapSlop: number; // px of movement still counted as a tap
  longPressMs: number;
  momentumDecay: number; // per 16ms frame
}

const DEFAULTS: GestureOptions = { tapSlop: 10, longPressMs: 550, momentumDecay: 0.94 };

type Mode = 'idle' | 'pending' | 'scroll' | 'pinch' | 'longpress';

export type GestureEvent =
  | { kind: 'input'; msg: InputMsg }
  | { kind: 'pinchPreview'; scale: number; cx: number; cy: number } // local visual feedback
  | { kind: 'pinchEnd' }
  | { kind: 'longPress' } // for haptic feedback
  | { kind: 'focusKeyboard' };

export class GestureRecognizer {
  private o: GestureOptions;
  private pts = new Map<number, Pt>();
  private mode: Mode = 'idle';
  private start?: { x: number; y: number; t: number };
  private last?: { x: number; y: number; t: number };
  private velocity = { x: 0, y: 0 };
  private pinchStartDist = 0;
  private pinchScale = 1;
  private pinchCenter = { x: 0, y: 0 };
  private longPressTimer?: ReturnType<typeof setTimeout>;
  private momentumTimer?: ReturnType<typeof setInterval>;

  constructor(private emit: (e: GestureEvent) => void, opts: Partial<GestureOptions> = {}, private now: () => number = () => performance.now()) {
    this.o = { ...DEFAULTS, ...opts };
  }

  down(p: Pt) {
    this.stopMomentum();
    this.pts.set(p.id, p);
    if (this.pts.size === 1) {
      this.mode = 'pending';
      const t = this.now();
      this.start = { x: p.x, y: p.y, t };
      this.last = { ...this.start };
      this.velocity = { x: 0, y: 0 };
      clearTimeout(this.longPressTimer);
      this.longPressTimer = setTimeout(() => this.fireLongPress(), this.o.longPressMs);
    } else if (this.pts.size === 2) {
      clearTimeout(this.longPressTimer);
      const [a, b] = [...this.pts.values()];
      this.mode = 'pinch';
      this.pinchStartDist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      this.pinchScale = 1;
      this.pinchCenter = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.last = { ...this.pinchCenter, t: this.now() };
    }
  }

  move(p: Pt) {
    if (!this.pts.has(p.id)) return;
    this.pts.set(p.id, p);
    const t = this.now();
    if (this.mode === 'pending' && this.start) {
      if (Math.hypot(p.x - this.start.x, p.y - this.start.y) > this.o.tapSlop) {
        clearTimeout(this.longPressTimer);
        this.mode = 'scroll';
      }
    }
    if (this.mode === 'scroll' && this.last) {
      const dx = this.last.x - p.x;
      const dy = this.last.y - p.y;
      const dt = Math.max(1, t - this.last.t);
      this.velocity = { x: (dx / dt) * 16, y: (dy / dt) * 16 };
      this.emit({ kind: 'input', msg: { t: 'wh', x: p.x, y: p.y, dx, dy } });
      this.last = { x: p.x, y: p.y, t };
    } else if (this.mode === 'pinch' && this.pts.size >= 2 && this.last) {
      const [a, b] = [...this.pts.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      this.pinchScale = dist / this.pinchStartDist;
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      // two-finger pan scrolls
      const dx = this.last.x - cx;
      const dy = this.last.y - cy;
      if (Math.abs(this.pinchScale - 1) < 0.08 && (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5)) {
        this.emit({ kind: 'input', msg: { t: 'wh', x: cx, y: cy, dx, dy } });
      }
      this.last = { x: cx, y: cy, t };
      this.emit({ kind: 'pinchPreview', scale: this.pinchScale, cx: this.pinchCenter.x, cy: this.pinchCenter.y });
    } else if (this.mode === 'longpress') {
      // drag after long press: move the (right-clicked) pointer for context-menu navigation
      this.emit({ kind: 'input', msg: { t: 'mm', x: p.x, y: p.y } });
    }
  }

  up(p: Pt) {
    if (!this.pts.has(p.id)) return;
    this.pts.delete(p.id);
    clearTimeout(this.longPressTimer);
    if (this.mode === 'pending' && this.start) {
      const { x, y } = this.start;
      this.emit({ kind: 'input', msg: { t: 'mm', x, y } });
      this.emit({ kind: 'input', msg: { t: 'md', x, y, b: 0 } });
      this.emit({ kind: 'input', msg: { t: 'mu', x, y, b: 0 } });
      this.emit({ kind: 'focusKeyboard' });
      this.mode = 'idle';
    } else if (this.mode === 'scroll') {
      this.mode = 'idle';
      if (this.now() - (this.last?.t ?? 0) < 80) this.startMomentum(p.x, p.y);
    } else if (this.mode === 'pinch') {
      if (this.pts.size === 0) {
        if (Math.abs(this.pinchScale - 1) >= 0.08) {
          this.emit({ kind: 'input', msg: { t: 'pinch', x: this.pinchCenter.x, y: this.pinchCenter.y, scale: Math.round(this.pinchScale * 1000) / 1000 } });
        }
        this.emit({ kind: 'pinchEnd' });
        this.mode = 'idle';
      }
    } else if (this.pts.size === 0) {
      this.mode = 'idle';
    }
  }

  cancel(p: Pt) {
    this.pts.delete(p.id);
    clearTimeout(this.longPressTimer);
    if (this.mode === 'pinch' && this.pts.size === 0) this.emit({ kind: 'pinchEnd' });
    if (this.pts.size === 0) this.mode = 'idle';
  }

  private fireLongPress() {
    if (this.mode !== 'pending' || !this.start) return;
    this.mode = 'longpress';
    const { x, y } = this.start;
    this.emit({ kind: 'longPress' });
    this.emit({ kind: 'input', msg: { t: 'mm', x, y } });
    this.emit({ kind: 'input', msg: { t: 'md', x, y, b: 2 } });
    this.emit({ kind: 'input', msg: { t: 'mu', x, y, b: 2 } });
  }

  private startMomentum(x: number, y: number) {
    let { x: vx, y: vy } = this.velocity;
    if (Math.hypot(vx, vy) < 2) return;
    this.momentumTimer = setInterval(() => {
      vx *= this.o.momentumDecay;
      vy *= this.o.momentumDecay;
      if (Math.hypot(vx, vy) < 0.5) return this.stopMomentum();
      this.emit({ kind: 'input', msg: { t: 'wh', x, y, dx: vx, dy: vy } });
    }, 16);
  }

  stopMomentum() {
    if (this.momentumTimer) clearInterval(this.momentumTimer);
    this.momentumTimer = undefined;
  }

  dispose() {
    clearTimeout(this.longPressTimer);
    this.stopMomentum();
  }
}
